import { adminApi } from '@/lib/api';

export const ADMIN_BADGE_KEYS = {
  SUPPORT_UNRESOLVED: 'support.unresolved',
  APPROVALS_PENDING: 'approvals.pending',
  EVENTS_PENDING: 'events.pending',
  ORGANIZER_REQUESTS_PENDING: 'organizer_requests.pending',
  REFUNDS_PENDING: 'refunds.pending',
  VIOLATIONS_ACTIVE: 'violations.active',
  NOTIFICATIONS_UNREAD: 'notifications.unread',
  PAYOUTS_PENDING: 'payouts.pending',
  MESSAGES_UNREAD: 'messages.unread',
  REVIEWS_PENDING: 'reviews.pending',
  ESCALATIONS_OPEN: 'escalations.open',
  CASES_BREACHED: 'cases.breached',
  KYC_PENDING: 'kyc.pending',
  SETTLEMENTS_PENDING: 'settlements.pending',
} as const;

export type AdminBadgeKey = (typeof ADMIN_BADGE_KEYS)[keyof typeof ADMIN_BADGE_KEYS];
export type AdminBadgeMap = Partial<Record<AdminBadgeKey, number>>;

const BADGE_CACHE_MS = 30_000;

let cachedBadges: AdminBadgeMap | null = null;
let cachedAt = 0;
let inflight: Promise<AdminBadgeMap> | null = null;

async function fetchBadgeCounts(): Promise<AdminBadgeMap> {
  const now = Date.now();
  if (cachedBadges && now - cachedAt < BADGE_CACHE_MS) {
    return cachedBadges;
  }

  if (inflight) {
    return inflight;
  }

  inflight = adminApi
    .badgeCounts()
    .then((response) => {
      const payload = response.data?.data ?? {};
      const next: AdminBadgeMap = {};

      for (const key of Object.values(ADMIN_BADGE_KEYS)) {
        const value = Number(payload[key] ?? 0);
        next[key] = Number.isFinite(value) ? Math.max(0, value) : 0;
      }

      cachedBadges = next;
      cachedAt = Date.now();
      return next;
    })
    .catch((error) => {
      cachedAt = Date.now();
      throw error;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export function invalidateAdminBadgeCache(): void {
  cachedBadges = null;
  cachedAt = 0;
}

export async function resolveAdminBadges(keys: AdminBadgeKey[]): Promise<AdminBadgeMap> {
  const uniqueKeys = Array.from(new Set(keys));
  const all = await fetchBadgeCounts();

  return uniqueKeys.reduce<AdminBadgeMap>((acc, key) => {
    acc[key] = all[key] ?? 0;
    return acc;
  }, {});
}
